<template>
  <div class="notification-layout" :class="{ 'notification-layout--detail-open': showDetail }">
    <div class="notification-list-panel">
      <div class="notification-search">
        <i class="icon-material-outline-search notification-search__icon"></i>
        <input
            v-model="filters.search"
            :placeholder="$t('general.search_in_actions')"
            type="text"
            class="notification-search__input"
            @input="filters.isTyping = true"
        />
      </div>

      <div class="notification-toolbar">
        <button class="notification-toolbar__btn notification-toolbar__btn--secondary"
                type="button" @click="markAllAsRead">
          <i class="uil-envelope-open"></i>
          <span class="notification-toolbar__label">{{ $t('general.make_all_read') }}</span>
        </button>
        <button class="notification-toolbar__btn notification-toolbar__btn--info"
                type="button" @click="selectAll">
          <i class="uil-list-ul"></i>
          <span class="notification-toolbar__label">{{ selectAllText }}</span>
        </button>
        <button class="notification-toolbar__btn notification-toolbar__btn--danger"
                type="button" @click="deleteChecked">
          <i class="uil-trash"></i>
          <span class="notification-toolbar__label">{{ $t('general.choosed_delete') }}</span>
        </button>
      </div>

      <NotificationListSkeleton v-if="loading" />

      <div v-else-if="!actions.length" class="notification-empty">
        <i class="uil-bell-slash"></i>
        <p>{{ $t('general.no_actions') || 'Bekleyen işlem bulunmamaktadır' }}</p>
      </div>

      <ul v-else class="notification-items">
        <li
            v-for="(action, index) in actions"
            :key="'action_list_item_' + action.id"
            class="notification-item"
            :class="{
              'notification-item--selected': selected?.id === action.id,
              'notification-item--unread': !action.is_read,
              'notification-item--read': action.is_read,
            }"
            @click="markAsRead(index)"
        >
          <div class="notification-item__checkbox" @click.stop>
            <input
                :id="'actions_checkbox_' + index"
                v-model="checked"
                :value="action.id"
                type="checkbox"
                class="notification-item__checkbox-input"
            />
          </div>
          <div class="notification-item__icon" :class="action.is_read ? 'notification-item__icon--read' : ''">
            <i class="icon-feather-bell"></i>
          </div>
          <div class="notification-item__content">
            <h5 class="notification-item__title" :class="action.is_read ? 'notification-item__title--read' : ''">
              <template v-if="typeof action.parsed_data.message !== 'undefined'">
                {{ action.parsed_data.message }}
              </template>
              <template v-else>
                <span v-for="(messageText, messageKey) in action.parsed_data" :key="messageKey">
                  {{ messageKey }}: {{ messageText }}
                </span>
              </template>
            </h5>
          </div>
        </li>
      </ul>
    </div>

    <NotificationDetailPanel
        :item="selected"
        :loading="detailLoading"
        :show-back="true"
        @back="onBackToList"
    />
  </div>
</template>

<script>
import {
  GET_ITEMS,
  UPDATE_ITEM_BY_ID,
  LOADING,
} from "@/core/services/store/REST.module";
import { confirmDialog } from "@/utils/confirmDialog";
import NotificationListSkeleton from "./NotificationListSkeleton.vue";
import NotificationDetailPanel from "./NotificationDetailPanel.vue";

export default {
  name: "ActionList",
  components: {
    NotificationListSkeleton,
    NotificationDetailPanel,
  },
  data() {
    return {
      loading: true,
      actions: [],
      selected: null,
      checked: [],
      showDetail: false,
      filters: {
        is_action: 1,
        search: "",
        isTyping: false,
      },
      selectAllText: this.$t("general.select_all"),
    };
  },
  computed: {
    detailLoading() {
      return this.$store.getters[LOADING];
    },
  },
  methods: {
    clearSelection() {
      this.selected = null;
      this.showDetail = false;
    },
    onBackToList() {
      this.clearSelection();
    },
    selectAll() {
      if (this.checked.length === this.actions.length) {
        this.checked = [];
        this.selectAllText = this.$t("general.select_all");
        return;
      }
      this.checked = this.actions.map((a) => a.id);
      this.selectAllText = this.$t("general.clear");
    },
    getActions() {
      this.loading = true;
      this.$store
          .dispatch(GET_ITEMS, {
            url: "api/notifications",
            filters: {
              is_action: this.filters.is_action,
              search: this.filters.search,
            },
            acceptPromise: false,
          })
          .then((result) => {
            if (typeof result.data !== "undefined") {
              let formattedItems = [];
              result.data.forEach((item) => {
                if (item.type == 1) {
                  item.parsed_data = JSON.parse(item.data);
                  item.is_read = !(item.read_at === null || item.read_at === "");
                } else {
                  item.parsed_data = {
                    message: item.name,
                    text: item.text,
                    webUrl: null,
                  };
                  item.is_read = item.pivot.is_read;
                }
                formattedItems.push(item);
              });
              this.actions = formattedItems;

              if (formattedItems.length === 0) {
                this.clearSelection();
              } else if (window.innerWidth > 768) {
                this.markAsRead(0);
              }

              this.loading = false;
            }
          });
    },
    markAsRead(index) {
      let action = this.actions[index];
      if (typeof action === "undefined") return;

      this.selected = action;
      this.showDetail = true;

      let type_one = [];
      let type_two = [];
      if (action.type == 1) {
        type_one.push(action.id);
      } else if (action.type == 2) {
        type_two.push(action.id);
      }
      if (type_one.length > 0) {
        this.$store
            .dispatch(UPDATE_ITEM_BY_ID, {
              url: "api/notification-update",
              contents: { notification_id: type_one },
              acceptPromise: false,
            })
            .then((response) => {
              if (response.status) {
                action.is_read = true;
              } else {
                this.$emit("error", response.data);
              }
            });
      }
      if (type_two.length > 0) {
        this.$store
            .dispatch(UPDATE_ITEM_BY_ID, {
              url: "api/notice-update",
              contents: { notification_id: type_two },
              acceptPromise: false,
            })
            .then((response) => {
              if (response.status) {
                action.is_read = true;
              } else {
                this.$emit("error", response.data);
              }
            });
      }
    },
    async markAllAsRead() {
      const confirmed = await confirmDialog({
        title: this.$t("general.are_you_sure"),
        message: this.$t("general.mark_all_read_confirmation") || "Tümünü okundu olarak işaretlemek istediğinizden emin misiniz?",
        confirmText: this.$t("general.yes"),
        cancelText: this.$t("general.cancel"),
        icon: "question",
      });
      if (!confirmed) return;
      this.$store
          .dispatch(UPDATE_ITEM_BY_ID, {
            url: "api/notification-update",
            contents: { notification_id: "all", is_action: 1 },
            acceptPromise: false,
          })
          .then((response) => {
            if (response.status) {
              this.actions.forEach((a) => { a.is_read = true; });
            } else {
              this.$emit("error", response.data);
            }
          });

      this.$store
          .dispatch(UPDATE_ITEM_BY_ID, {
            url: "api/notice-update",
            contents: { notification_id: "all" },
            acceptPromise: false,
          })
          .then((response) => {
            if (response.status) {
              this.actions.forEach((a) => { a.is_read = true; });
            } else {
              this.$emit("error", response.data);
            }
          });
    },
    async deleteChecked() {
      if (!this.checked.length) {
        this.$emit("error", "Herhangi bir aksiyon seçilmedi.");
        return;
      }
      const confirmed = await confirmDialog({
        title: this.$t("general.are_you_sure"),
        message: this.$t("general.sure_to_delete") || "Seçilenleri silmek istediğinizden emin misiniz?",
        confirmText: this.$t("general.yes"),
        cancelText: this.$t("general.cancel"),
        icon: "warning",
        confirmButtonColor: "#e31748",
      });
      if (!confirmed) return;
      this.$store
          .dispatch(UPDATE_ITEM_BY_ID, {
            url: "api/notification-delete",
            contents: {
              notification_id: this.checked,
              is_action: 1,
            },
            acceptPromise: false,
          })
          .then((response) => {
            if (response.status) {
              this.checked = [];
              this.clearSelection();
              this.getActions();
            } else {
              this.$emit("error", response.data);
            }
          });
    },
  },
  mounted() {
    this.getActions();
  },
  watch: {
    "filters.search": _.debounce(function () {
      this.filters.isTyping = false;
    }, 1000),
    "filters.isTyping": function (value) {
      if (!value) this.getActions();
    },
    actions: function (value) {
      let itemId = this.$route.params.id;
      if (itemId && value) {
        let idx = value.findIndex((item) => item.id === itemId);
        if (idx) {
          this.markAsRead(idx);
        }
      }
    },
  },
};
</script>

<style scoped>
.notification-layout {
  display: flex;
  gap: 20px;
  min-height: 500px;
}

.notification-list-panel {
  width: 480px;
  min-width: 480px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Search */
.notification-search {
  position: relative;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.notification-search__icon {
  position: absolute;
  left: 28px;
  top: 40%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
  pointer-events: none;
}

.notification-search__input {
  width: 100%;
  padding: 10px 14px 10px 38px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.notification-search__input::placeholder {
  color: #94a3b8;
}

.notification-search__input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  background: #fff;
}

/* Toolbar */
.notification-toolbar {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.notification-toolbar::-webkit-scrollbar {
  display: none;
}

.notification-toolbar__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
  white-space: nowrap;
}

.notification-toolbar__btn i {
  font-size: 15px;
}

.notification-toolbar__btn--secondary {
  color: #475569;
}

.notification-toolbar__btn--secondary:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.notification-toolbar__btn--info {
  color: #2563eb;
}

.notification-toolbar__btn--info:hover {
  background: #eff6ff;
  border-color: #2563eb;
}

.notification-toolbar__btn--danger {
  color: #dc2626;
}

.notification-toolbar__btn--danger:hover {
  background: #fef2f2;
  border-color: #dc2626;
}

/* Empty state */
.notification-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #94a3b8;
  padding: 48px 16px;
}

.notification-empty i {
  font-size: 48px;
  opacity: 0.4;
}

.notification-empty p {
  font-size: 14px;
  margin: 0;
}

/* List items */
.notification-items {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
}

.notification-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.15s ease;
}

.notification-item:hover {
  background: #f8fafc;
}

.notification-item--selected {
  background: #eff6ff;
}

.notification-item--selected:hover {
  background: #eff6ff;
}

.notification-item--unread {
  background: #fefce8;
}

.notification-item--unread:hover {
  background: #fef9c3;
}

.notification-item--unread.notification-item--selected {
  background: #eff6ff;
}

.notification-item__checkbox {
  flex-shrink: 0;
}

.notification-item__checkbox-input {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #2563eb;
}

.notification-item__checkbox-input:focus {
  box-shadow: none !important;
}

.notification-item__icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eff6ff;
  color: #2563eb;
  font-size: 16px;
  transition: all 0.2s ease;
}

.notification-item__icon--read {
  background: #f1f5f9;
  color: #94a3b8;
}

.notification-item__content {
  flex: 1;
  min-width: 0;
}

.notification-item__title {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  margin: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.notification-item__title--read {
  color: #64748b;
  font-weight: 400;
}

/* Responsive */
@media (max-width: 960px) {
  .notification-list-panel {
    width: 400px;
    min-width: 400px;
  }

  .notification-toolbar__btn {
    padding: 6px 10px;
  }
}

@media (max-width: 768px) {
  .notification-layout {
    gap: 0;
    position: relative;
  }

  .notification-list-panel {
    width: 100%;
    min-width: 100%;
    border-radius: 0;
    border: none;
  }

  .notification-layout--detail-open .notification-list-panel {
    display: none;
  }

  .notification-layout :deep(.notification-detail) {
    display: none;
  }

  .notification-layout--detail-open :deep(.notification-detail) {
    display: flex;
  }
}

@media (max-width: 480px) {
  .notification-search {
    padding: 12px;
  }

  .notification-search__input {
    font-size: 13px;
    padding: 8px 12px 8px 34px;
  }

  .notification-toolbar {
    padding: 10px 12px;
    gap: 6px;
  }

  .notification-toolbar__btn {
    padding: 5px 8px;
    font-size: 12px;
  }

  .notification-toolbar__btn i {
    font-size: 14px;
  }

  .notification-item {
    padding: 12px;
    gap: 10px;
  }

  .notification-item__icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .notification-item__title {
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .notification-search__input {
    min-height: 48px;
    box-sizing: border-box;
    padding: 12px 14px 12px 38px;
    font-size: 16px;
    border-radius: 10px;
  }

  .notification-toolbar__btn {
    min-height: 48px;
    box-sizing: border-box;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 14px;
  }

  .notification-item {
    min-height: 64px;
    box-sizing: border-box;
    padding: 12px;
  }

  .notification-item__checkbox {
    display: inline-flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    flex: 0 0 44px;
  }

  .notification-item__icon {
    width: 40px;
    height: 40px;
    font-size: 17px;
  }

  .notification-item__title {
    font-size: 15px;
    line-height: 1.45;
  }
}
</style>
